/*
 * Copyright (c) 2025 curryfirm.com
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import {
    fetchDumpsThunk,
    selectAllDumps,
    selectDumpsAreLoading,
    selectDumpsError,
} from "../../../features/dumps/dumpsSlice.js";
import {
    selectConfirmedDumpLocationIds,
    selectReturnToSummaryAvailable,
    setConfirmedDumpLocationIds,
    setReturnToSummaryAvailable,
    setStep,
    WIZARD_STEPS,
} from "../../../features/workflow/workflowSlice.js";
import { BACK, FORWARD } from "../../../constants.js";

/**
 * Display the list of dumps fetched from the Google Sheets doc.
 * Assume that they are at the location read and that all are available for use.
 * That is, start with their checkboxes checked.
 * Allow the user to deselect dumps that he or she does not want considered in
 * routing trucks to haul dumpsters to the dumps.
 * @param onBack
 * @param onContinue
 */
const ConfirmDumpLocations = ({ onBack, onContinue }) => {
    const dispatch = useDispatch();

    const previousSelection = useSelector(selectConfirmedDumpLocationIds);
    const choicesChanged = (chosenIds) => {
        return JSON.stringify(previousSelection) !== JSON.stringify(chosenIds);
    }

    // Selectors for global data and inventory
    const dumps = useSelector(selectAllDumps);
    const loading = useSelector(selectDumpsAreLoading);
    const error = useSelector(selectDumpsError);

    // Select workflow/session choice state
    const returnToSummaryAvailable = useSelector(selectReturnToSummaryAvailable);

    // Local state: initialized to previous session choices or empty
    const [chosenIds, setChosenIds] = useState(
        previousSelection.length > 0 ? previousSelection : []
    );

    // Ensure inventory is loaded.
    useEffect(() => {
        // Load dumps if not present
        if (dumps.length === 0 && !loading) {
            dispatch(fetchDumpsThunk());
        }
    }, [dispatch, dumps.length, loading]);

    // Initialize all checkboxes *if* not previously set, after inventory loads.
    useEffect(() => {
        if (
            previousSelection.length === 0 &&
            dumps.length > 0 &&
            chosenIds.length === 0
        ) {
            setChosenIds(dumps.map(d => d.locationId));
        }
    }, [previousSelection, dumps, chosenIds.length]);

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (loading) {
        return <Typography variant={"body1"}>Loading dumps...</Typography>;
    }

    const toggleSelection = (id) => {
        setChosenIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleLeaveStep = (direction) => {
        if (choicesChanged(chosenIds)) dispatch(setConfirmedDumpLocationIds(chosenIds));
        (direction === FORWARD) ? onContinue() : onBack();
    };

    const handleReturnToSummary = () => {
        if (choicesChanged(chosenIds)) dispatch(setConfirmedDumpLocationIds(chosenIds));
        dispatch(setStep(WIZARD_STEPS.SUMMARY));
        dispatch(setReturnToSummaryAvailable(false)); // Reset flag on return to summary
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Confirm Dumps in Play for This Routing</Typography>
            {
                dumps.length < 1
                    ? (
                        <Alert severity="info">
                            No dumps.  This will not go well!  Click Next to continue.
                        </Alert>
                    )
                    : (
                        <List>
                            {dumps.map(d => (
                                <ListItem
                                    key={d.locationId}
                                    secondaryAction={
                                        <Checkbox
                                            edge="end"
                                            checked={chosenIds.includes(d.locationId)}
                                            onChange={() => toggleSelection(d.locationId)}
                                        />
                                    }
                                >
                                    <Typography variant="body1">
                                        {d.dumpName}: {d.street}, {d.city}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    )
            }

            <Box sx={{ mt: 3, display: "flex", }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleLeaveStep(BACK)}
                >
                    Back
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleLeaveStep(FORWARD)}
                    sx={{ ml: 2 }}
                >
                    Next
                </Button>

                {/* Render a "Return to Summary" button conditionally, basd on store */}
                { returnToSummaryAvailable && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleReturnToSummary}
                        sx={{ ml: 2 }}
                    >
                        Return to Summary
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default ConfirmDumpLocations;
